<script setup>
import axios from 'axios';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { extractApiError } from '../apiError';
import { formatDateTime, formatMoney, formatTime } from '../format';
import { t } from '../i18n';
import { isPhone } from '../isPhone';
import { DEFAULT_PERIOD, isValidPeriod, localToday, periodRange } from '../calendarPeriod';
import { readUiState, writeUiState } from '../uiState';
import { accountView, payoutsForView } from '../portfolioView';
import {
  busy, currentView, dataVersion, error, fxRates, iisLimit, importDialogOpen, keyConfigured, loadPortfolios,
  loading, message, openGroupDialog, payoutGoals, portfolios, refresh, remove, select, views,
} from '../portfolioStore';
import AccountsCard from './AccountsCard.vue';
import AchievementsPage from './AchievementsPage.vue';
import AnalyticsPage from './AnalyticsPage.vue';
import BottomSheet from './BottomSheet.vue';
import LiveDot from './LiveDot.vue';
import MarketsPage from './MarketsPage.vue';
import InstrumentPage from './InstrumentPage.vue';
import NewsPage from './NewsPage.vue';
import OperationsPage from './OperationsPage.vue';
import PortfolioCard from './PortfolioCard.vue';
import PayoutCalendar from './PayoutCalendar.vue';
import PayoutGoalDialog from './PayoutGoalDialog.vue';
import PortfolioSelector from './PortfolioSelector.vue';
import PullToRefresh from './PullToRefresh.vue';

const props = defineProps({
  // 'portfolio' | 'cashflow' | 'analytics' | 'operations' | 'news' | 'markets' (docs/cashflow/SPEC.md decision 1,
  // docs/analytics/SPEC.md decision 1, docs/operations/SPEC.md decision 2, docs/news/SPEC.md decision 13,
  // docs/heatmap/SPEC.md decision 3)
  section: {
    type: String,
    default: 'portfolio',
  },
  // The instrument screen's uid when section is 'instrument' (docs/instrument/SPEC.md)
  instrumentUid: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['go-settings', 'navigate', 'toast', 'open-instrument']);

// Screens inside the «Портфель» tab, opened from the buttons under the total: back arrow, «Портфель» stays active.
// The desktop section tabs live in the app header (App.vue, docs/heatmap/SPEC.md decision 34).
const NESTED_SECTIONS = ['operations', 'cashflow', 'analytics', 'achievements', 'instrument'];
const isNested = computed(() => NESTED_SECTIONS.includes(props.section));

// Phone: the portfolio pill and the price dot open bottom sheets (docs/design/SPEC.md, stage M1).
const selectorSheetOpen = ref(false);
const liveSheetOpen = ref(false);

const selectorSections = computed(() => [
  { key: 'capital', title: null, items: views.value.filter((v) => v.kind === 'capital') },
  { key: 'accounts', title: t('portfolios.accountsSection'), items: views.value.filter((v) => v.kind === 'account') },
  { key: 'groups', title: t('portfolios.groupsSection'), items: views.value.filter((v) => v.kind === 'group') },
].filter((s) => s.items.length > 0));

function viewLabel(view) {
  return view.name || view.account_id;
}

function chooseView(key) {
  selectorSheetOpen.value = false;
  select(key);
  window.scrollTo({ top: 0 });
}

// A nested screen → back to where it was opened from (docs/instrument/SPEC.md decision 1, docs/design/SPEC.md
// decision 32); opened by a link with no step behind it — to the home screen.
function goBack() {
  if ((window.history.state?.depth ?? 0) > 0) {
    window.history.back();
    return;
  }
  emit('navigate', 'portfolio');
}

function openFromSheet(action) {
  selectorSheetOpen.value = false;
  action();
}

function refreshFromSheet() {
  liveSheetOpen.value = false;
  refresh(currentView.value.account_ids);
}

// Phone home of "Capital" or a composite portfolio: its accounts (docs/design/SPEC.md, «Счета»).
const viewAccounts = computed(() => {
  const view = currentView.value;
  if (!view || view.kind === 'account') {
    return [];
  }
  const ids = new Set(view.members.map((m) => m.portfolio_id));
  return portfolios.value.filter((p) => ids.has(p.id)).map(accountView);
});

// Pull to refresh on the news screen re-reads the feed (docs/news/SPEC.md); on «Рынки» it re-reads the
// heatmap and lays the tiles out anew (docs/heatmap/SPEC.md, decision 9).
const newsVersion = ref(0);
const marketsVersion = ref(0);
const instrumentVersion = ref(0);
// Phone header title of the instrument screen: its ticker or the quote's name.
const instrumentTitle = ref('');
watch(() => props.instrumentUid, () => { instrumentTitle.value = ''; });

// Pull to refresh: the selected portfolio's accounts from T-Invest (decision 10); without a key the
// app only re-reads its own data.
async function onPull() {
  if (props.section === 'news') {
    newsVersion.value++;
    return;
  }
  if (props.section === 'markets') {
    marketsVersion.value++;
    return;
  }
  if (props.section === 'instrument') {
    instrumentVersion.value++;
    return;
  }
  if (keyConfigured.value && currentView.value.account_ids.length > 0) {
    await refresh(currentView.value.account_ids);
    return;
  }
  await loadPortfolios({ silent: true });
  emit('toast', t('refresh.noKey'), 'info');
}

// Payout calendar (docs/cashflow/SPEC.md, decisions 11–19). The period is remembered in the browser.
function readCalendarPeriod() {
  const saved = readUiState('calendar.period', null);
  return isValidPeriod(saved) ? saved : { ...DEFAULT_PERIOD };
}

const calendarPeriod = ref(readCalendarPeriod());
watch(calendarPeriod, (value) => writeUiState('calendar.period', value), { deep: true });
const calendarItems = ref([]);
const calendarYears = ref([]);
// Moscow "today" from the server; the browser's date until the first answer
const calendarToday = ref(localToday());
const calendarLoading = ref(false);
const calendarError = ref('');
let calendarRequest = 0;

const currentCalendarItems = computed(() => payoutsForView(currentView.value, calendarItems.value));

async function loadCalendar() {
  if (portfolios.value.length === 0) {
    calendarItems.value = [];
    return;
  }
  const request = ++calendarRequest;
  calendarLoading.value = true;
  calendarError.value = '';
  try {
    const { data } = await axios.get('/cashflow/calendar', { params: periodRange(calendarPeriod.value, calendarToday.value) });
    // A slower answer for an older period must not overwrite a newer one.
    if (request === calendarRequest) {
      calendarItems.value = data.items;
      calendarYears.value = data.years;
      calendarToday.value = data.today;
    }
  } catch (e) {
    if (request === calendarRequest && e?.response?.status !== 401) {
      calendarError.value = extractApiError(e, t('calendar.loadFailed'));
    }
  } finally {
    if (request === calendarRequest) {
      calendarLoading.value = false;
    }
  }
}

watch(calendarPeriod, loadCalendar, { deep: true });

// Payouts for the next 12 months regardless of the calendar's period: passive income in the
// analytics summary (docs/analytics/SPEC.md, decision 12).
const forwardItems = ref([]);
const currentForwardItems = computed(() => payoutsForView(currentView.value, forwardItems.value));

async function loadForward() {
  if (portfolios.value.length === 0) {
    forwardItems.value = [];
    return;
  }
  try {
    const { data } = await axios.get('/cashflow/calendar', { params: periodRange(DEFAULT_PERIOD, localToday()) });
    forwardItems.value = data.items;
  } catch {
    // The summary shows "—" for passive income; the calendar reports load errors itself.
    forwardItems.value = [];
  }
}

// Accounts changed (load, import, removal): what depends on them is read again.
watch(dataVersion, () => {
  loadCalendar();
  loadForward();
}, { immediate: true });

// Payout goals, one per view key (docs/payouts/SPEC.md, decision 12).
const goalDialogOpen = ref(false);
const goalSaving = ref(false);
const goalError = ref('');

const currentGoal = computed(() => payoutGoals.value.find((g) => g.scope === currentView.value.key) ?? null);

function openGoalDialog() {
  goalError.value = '';
  goalDialogOpen.value = true;
}

async function sendGoal(request) {
  goalSaving.value = true;
  goalError.value = '';
  try {
    const { data } = await request();
    payoutGoals.value = data.goals;
    goalDialogOpen.value = false;
  } catch (e) {
    if (e?.response?.status !== 401) {
      goalError.value = extractApiError(e, t('goal.saveFailed'));
    }
  } finally {
    goalSaving.value = false;
  }
}

function saveGoal({ period, amount }) {
  sendGoal(() => axios.put('/payout-goals', { scope: currentView.value.key, period, amount }));
}

function removeGoal() {
  sendGoal(() => axios.delete('/payout-goals', { data: { scope: currentView.value.key } }));
}

// Live prices (docs/live-prices/SPEC.md, decisions 8, 9): polled from our cache while the tab is visible.
const LIVE_POLL_MS = 5000;
const livePrices = ref({});
const liveOn = ref(false);
const liveCheckedAt = ref(null);
let livePollTimer = null;
let livePollInFlight = false;

// Indicator state (docs/design/SPEC.md, decision 8): 'on' while the daemon is alive and our last
// successful poll is fresh, 'off' when offline, the poll failed or no answer for 15 s.
const LIVE_STALE_MS = 15000;
const nowMs = ref(Date.now());
const online = ref(navigator.onLine !== false);

const liveState = computed(() => {
  if (liveCheckedAt.value === null) {
    return null;
  }
  const fresh = nowMs.value - Date.parse(liveCheckedAt.value) <= LIVE_STALE_MS;
  return liveOn.value && fresh && online.value ? 'on' : 'off';
});

function onOnlineChange() {
  online.value = navigator.onLine !== false;
  nowMs.value = Date.now();
}

async function pollLivePrices() {
  nowMs.value = Date.now();
  if (livePollInFlight || portfolios.value.length === 0) {
    return;
  }
  livePollInFlight = true;
  try {
    const { data } = await axios.get('/portfolios/live-prices');
    livePrices.value = data.prices ?? {};
    liveOn.value = Boolean(data.live);
    liveCheckedAt.value = new Date().toISOString();
    nowMs.value = Date.now();
  } catch {
    // Not critical: the cards keep showing the last prices or the snapshot
    liveOn.value = false;
  } finally {
    livePollInFlight = false;
  }
}

function startLivePolling() {
  if (livePollTimer !== null || document.visibilityState !== 'visible') {
    return;
  }
  pollLivePrices();
  livePollTimer = setInterval(pollLivePrices, LIVE_POLL_MS);
}

function stopLivePolling() {
  if (livePollTimer !== null) {
    clearInterval(livePollTimer);
    livePollTimer = null;
  }
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible') {
    startLivePolling();
  } else {
    stopLivePolling();
  }
}

const shownPrices = computed(() => (liveOn.value ? livePrices.value : {}));

// New or removed portfolios change the instrument set: ask for prices right away.
watch(
  () => portfolios.value.map((p) => p.id).join(','),
  () => {
    if (portfolios.value.length === 0) {
      livePrices.value = {};
    }
    pollLivePrices();
  }
);

onMounted(() => {
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('online', onOnlineChange);
  window.addEventListener('offline', onOnlineChange);
  startLivePolling();
});

onUnmounted(() => {
  document.removeEventListener('visibilitychange', onVisibilityChange);
  window.removeEventListener('online', onOnlineChange);
  window.removeEventListener('offline', onOnlineChange);
  stopLivePolling();
});
</script>

<template>
  <div class="w-full space-y-3 sm:space-y-6">
    <!-- Phone header content: portfolio pill in the middle, price dot on the right (docs/design/SPEC.md, stage M1). -->
    <Teleport
      defer
      to="#mobile-header"
    >
      <div
        v-if="!loading && portfolios.length > 0"
        class="grid h-full grid-cols-[44px_minmax(0,1fr)_44px] items-center"
      >
        <button
          v-if="isNested"
          type="button"
          class="flex size-11 items-center justify-center text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          :aria-label="t('operations.back')"
          @click="goBack"
        >
          <svg
            class="size-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="m15 5-7 7 7 7" />
          </svg>
        </button>
        <span
          v-else
          aria-hidden="true"
        />
        <!-- News are not about the selected portfolio (docs/news/SPEC.md, decision 14): a title, not the selector -->
        <span
          v-if="section === 'news'"
          class="truncate text-center text-[1.0625rem] font-semibold text-slate-100"
        >{{ t('news.title') }}</span>
        <span
          v-else-if="section === 'instrument'"
          class="truncate text-center text-[1.0625rem] font-semibold text-slate-100"
        >{{ instrumentTitle }}</span>
        <button
          v-else
          type="button"
          class="relative mx-auto flex h-8 max-w-full items-center gap-1 rounded-full bg-slate-900 px-3.5 text-[0.9375rem] font-medium text-slate-100 before:absolute before:inset-x-0 before:-inset-y-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          aria-haspopup="dialog"
          :aria-label="t('selector.aria', { name: viewLabel(currentView) })"
          @click="selectorSheetOpen = true"
        >
          <span class="truncate">{{ viewLabel(currentView) }}</span>
          <svg
            class="size-4 shrink-0 text-slate-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
        <!-- Live prices have nothing to do with the operations list (docs/operations/DESIGN.md) -->
        <span
          v-if="section === 'operations' || section === 'news'"
          aria-hidden="true"
        />
        <LiveDot
          v-else
          :state="liveState"
          :time="liveCheckedAt ? formatTime(liveCheckedAt) : ''"
          @open="liveSheetOpen = true"
        />
      </div>
    </Teleport>

    <!-- On a phone messages and errors are toasts (App.vue) -->
    <p
      v-if="message"
      class="hidden text-sm text-emerald-400 sm:block"
    >
      {{ message }}
    </p>
    <p
      v-if="error"
      class="hidden text-sm text-rose-400 sm:block"
    >
      {{ error }}
    </p>

    <template v-if="loading">
      <!-- Phone: skeleton instead of «Загрузка…» -->
      <div
        class="space-y-3 sm:hidden"
        aria-busy="true"
      >
        <span class="sr-only">{{ t('common.loading') }}</span>
        <div class="rounded-3xl bg-slate-900 p-5">
          <div class="h-9 w-52 rounded-lg bg-slate-800 motion-safe:animate-pulse" />
          <div class="mt-3 h-4 w-40 rounded bg-slate-800 motion-safe:animate-pulse" />
        </div>
        <div class="space-y-2 rounded-3xl bg-slate-900 p-5">
          <div
            v-for="n in 5"
            :key="n"
            class="h-14 rounded-xl bg-slate-800 motion-safe:animate-pulse"
          />
        </div>
      </div>
      <p class="hidden text-sm text-slate-400 sm:block">
        {{ t('common.loading') }}
      </p>
    </template>

    <div
      v-else-if="error && portfolios.length === 0"
      class="rounded-3xl bg-slate-900 p-6 text-center sm:hidden"
    >
      <p class="text-[0.9375rem] text-rose-300">
        {{ error }}
      </p>
      <button
        type="button"
        class="mt-4 h-11 w-full rounded-2xl bg-slate-800 text-[0.9375rem] font-medium text-slate-100 active:bg-slate-700"
        @click="loadPortfolios()"
      >
        {{ t('common.retry') }}
      </button>
    </div>

    <div
      v-else-if="!keyConfigured && portfolios.length === 0"
      class="rounded-3xl bg-slate-900 p-6 text-center sm:rounded-lg sm:border sm:border-dashed sm:border-slate-700 sm:bg-transparent sm:p-8"
    >
      <p class="text-[1.0625rem] font-semibold text-slate-100 sm:hidden">
        {{ t('empty.connectTitle') }}
      </p>
      <p class="mt-2 text-[0.9375rem] text-slate-300 sm:mt-0 sm:text-sm">
        {{ t('portfolios.needKey') }}
      </p>
      <button
        type="button"
        class="mt-4 h-11 w-full rounded-2xl bg-emerald-600 text-[0.9375rem] font-medium hover:bg-emerald-500 sm:h-auto sm:w-auto sm:rounded sm:px-4 sm:py-2 sm:text-sm"
        @click="emit('go-settings')"
      >
        {{ t('portfolios.openSettings') }}
      </button>
    </div>

    <div
      v-else-if="portfolios.length === 0"
      class="rounded-3xl bg-slate-900 p-6 text-center sm:rounded-lg sm:border sm:border-dashed sm:border-slate-700 sm:bg-transparent sm:p-8"
    >
      <p class="text-[1.0625rem] font-semibold text-slate-100 sm:hidden">
        {{ t('empty.loadTitle') }}
      </p>
      <p class="mt-2 text-[0.9375rem] text-slate-300 sm:mt-0 sm:text-sm">
        {{ t('portfolios.empty') }}
      </p>
      <button
        type="button"
        class="mt-4 h-11 w-full rounded-2xl bg-emerald-600 text-[0.9375rem] font-medium hover:bg-emerald-500 sm:h-auto sm:w-auto sm:rounded sm:px-4 sm:py-2 sm:text-sm"
        @click="importDialogOpen = true"
      >
        {{ t('portfolios.load') }}
      </button>
    </div>

    <template v-else>
      <!-- Desktop way back from payouts and analytics, as on the operations screen (docs/design/SPEC.md,
           decision 25); sticks under the header so it is there after scrolling down (decision 26).
           A phone goes back with the «‹» in its header or a swipe -->
      <div
        v-if="section === 'cashflow' || section === 'analytics' || section === 'achievements' || section === 'instrument'"
        class="sticky top-[calc(3.5rem+env(safe-area-inset-top))] z-30 mb-0 hidden bg-slate-950/90 py-2 backdrop-blur-md sm:block"
      >
        <button
          type="button"
          class="text-sm text-slate-400 hover:text-slate-200"
          @click="goBack"
        >
          ‹ {{ section === 'instrument' ? t('operations.back') : t('operations.backHome') }}
        </button>
      </div>
      <!-- The instrument screen is not about the selected view's list: no selector (its «У вас» follows it) -->
      <div
        v-if="section !== 'instrument'"
        class="hidden space-y-1.5 sm:block"
      >
        <!-- A centred pill with the live price dot before the name, as on a phone (docs/design/SPEC.md, decisions 30, 31) -->
        <PortfolioSelector
          :views="views"
          :selected-key="currentView.key"
          :live-state="liveState"
          :live-label="liveState === 'on' ? t('live.on', { time: liveCheckedAt ? formatTime(liveCheckedAt) : '' }) : t('live.off')"
          @select="select"
          @create-group="openGroupDialog()"
        />
        <p
          v-if="!keyConfigured"
          class="min-w-0 truncate text-xs text-amber-400"
          :title="t('portfolios.keyRemoved')"
        >
          {{ t('portfolios.keyRemovedShort') }}
          <button
            type="button"
            class="ml-1 underline decoration-dotted underline-offset-2 hover:text-amber-300"
            @click="emit('go-settings')"
          >
            {{ t('app.settings') }}
          </button>
        </p>
      </div>

      <PullToRefresh
        :busy="isPhone && busy"
        @refresh="onPull"
      >
        <template v-if="section === 'portfolio'">
          <PortfolioCard
            :key="currentView.key"
            :portfolio="currentView"
            :busy="busy"
            :can-refresh="keyConfigured && currentView.account_ids.length > 0"
            :live-prices="shownPrices"
            :fx-rates="fxRates"
            :iis="iisLimit"
            @refresh="refresh(currentView.account_ids)"
            @edit="openGroupDialog(currentView.group)"
            @remove="remove"
            @navigate="(target) => emit('navigate', target)"
            @open-instrument="(uid) => emit('open-instrument', uid)"
            @toast="(...args) => emit('toast', ...args)"
          >
            <AccountsCard
              v-if="isPhone && viewAccounts.length > 1"
              :accounts="viewAccounts"
              :live-prices="shownPrices"
              :fx-rates="fxRates"
              @select="chooseView"
            />
          </PortfolioCard>
        </template>
        <OperationsPage
          v-else-if="section === 'operations'"
          :portfolio="currentView"
          :desktop="!isPhone"
          :key-configured="keyConfigured"
          :busy="busy"
          :data-version="dataVersion"
          @back="goBack"
          @refresh="refresh(currentView.account_ids)"
          @go-settings="emit('go-settings')"
        />
        <NewsPage
          v-else-if="section === 'news'"
          :desktop="!isPhone"
          :data-version="newsVersion"
        />
        <MarketsPage
          v-else-if="section === 'markets'"
          :portfolio="currentView"
          :fx-rates="fxRates"
          :desktop="!isPhone"
          :data-version="marketsVersion"
          @toast="(...args) => emit('toast', ...args)"
          @open-instrument="(uid) => emit('open-instrument', uid)"
        />
        <InstrumentPage
          v-else-if="section === 'instrument' && instrumentUid"
          :key="instrumentUid"
          :uid="instrumentUid"
          :live-prices="shownPrices"
          :desktop="!isPhone"
          :data-version="instrumentVersion"
          @title="(title) => { instrumentTitle = title; }"
        />
        <AchievementsPage
          v-else-if="section === 'achievements'"
          :portfolio="currentView"
          :data-version="dataVersion"
        />
        <AnalyticsPage
          v-else-if="section === 'analytics'"
          :portfolio="currentView"
          :live-prices="shownPrices"
          :fx-rates="fxRates"
          :forward-payouts="currentForwardItems"
        />
        <template v-else>
          <div
            v-if="calendarError"
            class="mb-3 flex items-center justify-between gap-3 rounded-3xl bg-slate-900 p-5 text-[0.9375rem] text-rose-300 sm:mb-4 sm:rounded-none sm:bg-transparent sm:p-0 sm:text-sm sm:text-rose-400"
          >
            <span>{{ calendarError }}</span>
            <button
              type="button"
              class="h-11 shrink-0 rounded-2xl bg-slate-800 px-4 text-[0.9375rem] font-medium text-slate-100 sm:h-auto sm:rounded sm:py-1.5 sm:text-sm"
              @click="loadCalendar"
            >
              {{ t('common.retry') }}
            </button>
          </div>
          <PayoutCalendar
            v-model:period="calendarPeriod"
            :items="currentCalendarItems"
            :years="calendarYears"
            :today="calendarToday"
            :loading="calendarLoading"
            :goal="currentGoal"
            :forward-items="currentForwardItems"
            :portfolio="currentView"
            :live-prices="shownPrices"
            :fx-rates="fxRates"
            @edit-goal="openGoalDialog"
          />
        </template>
      </PullToRefresh>
    </template>

    <BottomSheet
      :open="selectorSheetOpen"
      :title="t('selector.title')"
      @close="selectorSheetOpen = false"
    >
      <div
        v-for="part in selectorSections"
        :key="part.key"
        class="border-t border-slate-800 first:border-t-0"
      >
        <p
          v-if="part.title"
          class="px-5 pb-1.5 pt-4 text-[0.8125rem] uppercase tracking-wide text-slate-500"
        >
          {{ part.title }}
        </p>
        <button
          v-for="view in part.items"
          :key="view.key"
          type="button"
          class="flex min-h-12 w-full items-center gap-3 px-5 py-2 text-left text-[0.9375rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 active:bg-slate-800"
          :aria-current="view.key === currentView.key ? 'true' : undefined"
          @click="chooseView(view.key)"
        >
          <!-- The tick follows the name, so the sums keep one right edge -->
          <span class="flex min-w-0 flex-1 items-center gap-1.5">
            <span
              class="line-clamp-2 min-w-0"
              :class="view.key === currentView.key ? 'font-semibold text-slate-100' : 'text-slate-200'"
            >{{ viewLabel(view) }}</span>
            <svg
              v-if="view.key === currentView.key"
              class="size-4 shrink-0 text-emerald-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="m5 12.5 4.5 4.5L19 7.5" />
            </svg>
          </span>
          <span
            class="shrink-0 tabular-nums"
            :class="view.key === currentView.key ? 'text-slate-100' : 'text-slate-400'"
          >{{ formatMoney(view.total_amount, view.currency) }}</span>
        </button>
      </div>
      <div class="mt-2 border-t border-slate-800">
        <button
          type="button"
          class="flex min-h-12 w-full items-center justify-between px-5 text-left text-[0.9375rem] text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 active:bg-slate-800"
          @click="openFromSheet(() => emit('go-settings'))"
        >
          {{ t('selector.manage') }}
          <span
            class="text-slate-500"
            aria-hidden="true"
          >›</span>
        </button>
      </div>
    </BottomSheet>

    <BottomSheet
      :open="liveSheetOpen"
      :title="t('live.title')"
      @close="liveSheetOpen = false"
    >
      <div class="space-y-3 px-5 pb-2 text-[0.9375rem]">
        <p
          class="flex items-start gap-3"
          :class="liveState === 'on' ? 'text-slate-100' : 'text-amber-300'"
        >
          <span
            class="mt-[7px] size-2.5 shrink-0 rounded-full"
            :class="liveState === 'on' ? 'bg-emerald-400' : 'border-2 border-amber-400'"
            aria-hidden="true"
          />
          <span>{{ liveState === 'on' ? t('live.onText', { time: liveCheckedAt ? formatTime(liveCheckedAt) : '' }) : t('live.offText') }}</span>
        </p>
        <p
          v-if="currentView?.synced_at"
          class="text-[0.8125rem] text-slate-400"
        >
          {{ t('live.snapshot', { date: formatDateTime(currentView.synced_at) }) }}
        </p>
        <button
          v-if="keyConfigured && currentView?.account_ids.length > 0"
          type="button"
          class="h-11 w-full rounded-2xl bg-slate-800 text-[0.9375rem] font-medium text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 active:bg-slate-700 disabled:opacity-50"
          :disabled="busy"
          @click="refreshFromSheet"
        >
          {{ busy ? t('refresh.running') : t('refresh.now') }}
        </button>
        <template v-else-if="!keyConfigured">
          <p class="text-[0.8125rem] text-amber-300">
            {{ t('refresh.noKey') }}
          </p>
          <button
            type="button"
            class="h-11 w-full rounded-2xl bg-slate-800 text-[0.9375rem] font-medium text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 active:bg-slate-700"
            @click="liveSheetOpen = false; emit('go-settings')"
          >
            {{ t('live.openProfile') }}
          </button>
        </template>
      </div>
    </BottomSheet>

    <PayoutGoalDialog
      v-if="portfolios.length > 0"
      :open="goalDialogOpen"
      :goal="currentGoal"
      :portfolio-name="currentView.name || currentView.account_id || ''"
      :saving="goalSaving"
      :error="goalError"
      @close="goalDialogOpen = false"
      @save="saveGoal"
      @remove="removeGoal"
    />
  </div>
</template>
